/**
 * useActiveProject — Surface Layer (/01)
 *
 * The active white-label project context. A tenant runs multiple projects;
 * the selected one scopes the workspace (RAG namespace, white-label theme,
 * per-project documents/reports). Persisted to the LocalDeviceStore
 * (localStorage today, SwiftData via the WebKit bridge tomorrow). The
 * server remains the source of truth for which projects exist; this is a
 * UX selection cache only.
 */

import { create } from 'zustand';

export interface ActiveProject {
  id: string;
  slug: string;
  displayLabel: string;
  ragNamespace: string | null;
  whiteLabel: Record<string, unknown> | null;
}

const STORAGE_KEY = 'surface.activeProject.v1';

interface ActiveProjectApi {
  active: ActiveProject | null;
  setActive: (project: ActiveProject | null) => void;
}

function readStored(): ActiveProject | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveProject>;
    if (typeof parsed?.id === 'string' && typeof parsed.slug === 'string') {
      return {
        id: parsed.id,
        slug: parsed.slug,
        displayLabel: typeof parsed.displayLabel === 'string' ? parsed.displayLabel : parsed.slug,
        ragNamespace: typeof parsed.ragNamespace === 'string' ? parsed.ragNamespace : null,
        whiteLabel:
          parsed.whiteLabel && typeof parsed.whiteLabel === 'object'
            ? (parsed.whiteLabel as Record<string, unknown>)
            : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStored(project: ActiveProject | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (project) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Selection cache only — the project list is owned by the backend contract.
  }
}

export const useActiveProjectStore = create<ActiveProjectApi>((set) => ({
  active: readStored(),
  setActive: (project) => {
    writeStored(project);
    set({ active: project });
  },
}));
